import _ from 'lodash';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {addQueryToPath} from '../../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {useDebounce} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {useInitialEffect} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  buildCancelTokenSource,
  CancelError,
  CancelToken,
  CancelTokenSource,
} from '../../../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {fetchApiAsync} from '../../../../../libs/shared/core/src/services/http/http';
import {DropdownEmpty} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownEmpty';
import {DropdownLoader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownLoader';
import {
  DropdownVirtualList,
  ScrollHandler,
} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {AsyncLoader} from '../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ApiGif, GifModel} from '../../../../models/api/gifModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {DropdownResults} from '../source/dropdownSource';
import {dropdownListHeight, dropdownWidth, gifsPerRow, gridSpacing, rowHeight} from './gifDropdownCommons';
import {GifDropdownListRow} from './gifDropdownListRow';

/*
 * Props.
 */

interface GifDropdownListProps {
  query: string;
  onGifSelect: (model: GifModel) => void;
}

/*
 * Styles.
 */

const StyledWrapperDiv = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  height: 100%;

  /* This bottom padding makes up the very bottom edge of the grid outline. */
  padding: 0 0 ${gridSpacing}px;

  // Dirty way to remove padding from dropdownVirtualList.
  > div > div {
    padding: 0;
  }
`;

/*
 * Component.
 */

export const GifDropdownList: FC<GifDropdownListProps> = ({query, onGifSelect}) => {
  const company = useAppSelector(getCompany);
  const trimmedQueryLength = useMemo(() => query.trim().length, [query]);

  const [status, setStatus] = useState(AsyncStatusesEnum.LOADING);
  const [results, setResults] = useState<DropdownResults | undefined>();

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = () => {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    // Only fetch if there's a query.
    if (trimmedQueryLength > 0) {
      fetchGifsAsync(query);
    }

    return () => maybeCancelRequest();
  });

  useEffect(() => {
    fetchGifsAsyncDebounced(query);

    // If query is reset, reset the status to LOADING to avoid displaying older GIFs.
    if (trimmedQueryLength === 0) {
      setStatus(AsyncStatusesEnum.LOADING);
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGifsAsync = async (searchQuery: string) => {
    const trimmedSearchQuery = searchQuery.trim();

    // Return early if search query is empty.
    if (trimmedSearchQuery.length === 0) {
      return;
    }

    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    try {
      const apiResults = await gifsFetcher(
        trimmedSearchQuery,
        '',
        onGifSelect,
        [],
        cancelTokenSourceRef.current.token,
        company.url,
      );

      cancelTokenSourceRef.current = undefined;

      setResults(apiResults);
      setStatus(AsyncStatusesEnum.LOADED);
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      cancelTokenSourceRef.current = undefined;

      setStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const fetchGifsAsyncDebounced = useDebounce(fetchGifsAsync, 300);

  const maybeLoadMoreAsync = async () => {
    // If there's already a request in progress, of if we can't load more, nothing to do.
    if (cancelTokenSourceRef.current || !results || !results.canLoadMore || !results.loadMoreAsync) {
      return;
    }

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    try {
      const newResults = await results.loadMoreAsync(cancelTokenSourceRef.current.token);

      cancelTokenSourceRef.current = undefined;

      setResults(newResults);
    } catch (error) {
      // Http cancel errors are expected.
      if (error instanceof CancelError) {
        return;
      }

      cancelTokenSourceRef.current = undefined;

      setStatus(AsyncStatusesEnum.FAILED);
    }
  };

  const onScroll: ScrollHandler = (scrollContext) => {
    // Only load more if we're near the bottom of the list.
    const {scrollTop, clientHeight, scrollHeight} = scrollContext;
    const isNearBottom = scrollTop + clientHeight > scrollHeight - 100;
    if (!isNearBottom) {
      return;
    }

    maybeLoadMoreAsync();
  };

  const renderResultsRow = (dropdownResults: DropdownResults, index: number) => {
    // If we're at the end of the list and we can load more, render a loader.
    if (dropdownResults.canLoadMore && index === dropdownResults.rowCount) {
      return (
        <AsyncLoader
          status={AsyncStatusesEnum.LOADING}
          size={VisualSizesEnum.LARGE}
          onRetryClick={() => fetchGifsAsync(query)}
        />
      );
    }

    // Otherwise, render a regular row.
    // We can set `isSearching` to true as we're only displaying results if the user searched for GIFs.
    return dropdownResults.renderRow(index, true);
  };

  const getRowHeight = (dropdownResults: DropdownResults, index: number) =>
    dropdownResults.getRowHeight(index);

  // If there's no query, return a placeholder.
  if (trimmedQueryLength === 0) {
    return (
      <DropdownEmpty>
        <Trans id="dropdown-gif-search">Search for GIFs</Trans>
      </DropdownEmpty>
    );
  }

  if (status !== AsyncStatusesEnum.LOADED) {
    return <DropdownLoader status={status} onRetryClick={() => fetchGifsAsync(query)} />;
  }

  // Empty state.
  if (!results || results.rowCount === 0) {
    return <DropdownEmpty />;
  }

  return (
    <StyledWrapperDiv>
      <DropdownVirtualList
        width={dropdownWidth}
        maxHeight={dropdownListHeight}
        minHeight={dropdownListHeight}
        rowCount={results.rowCount}
        renderRow={(index) => renderResultsRow(results, index)}
        getRowHeight={(index) => getRowHeight(results, index)}
        onScroll={onScroll}
      />
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

const gifsFetcher = async (
  query: string,
  nextToken: string,
  onGifSelect: (model: GifModel) => void,
  initialItems: Array<Array<GifModel>>,
  cancelToken: CancelToken,
  companyUrl: string,
): Promise<DropdownResults> => {
  const normalizedInitialItems = initialItems.reduce((acc, curr) => [...acc, ...curr], []);
  const url = buildGifSearchUrl(companyUrl, query, nextToken);
  const apiResults = await fetchApiAsync(ApiGif, url, {cancelToken});
  const chunks = _.chunk([...normalizedInitialItems, ...apiResults.data], gifsPerRow);

  const canLoadMore = Boolean(apiResults.pagination.offset);

  return {
    query,
    rowCount: chunks.length,
    canLoadMore,
    renderRow: (index: number) => (
      <GifDropdownListRow gifs={chunks[index]} onGifSelect={onGifSelect} shouldFillRow={canLoadMore} />
    ),
    getRowHeight: () => rowHeight,
    renderEmpty: () => null,
    loadMoreAsync: async (token: CancelToken) =>
      gifsFetcher(query, apiResults.pagination.offset, onGifSelect, chunks, token, companyUrl),
  };
};

const buildGifSearchUrl = (companyUrl: string, query: string, offset?: string) => {
  const path = `${companyUrl}/search_gifs2`;
  const queryParams = {
    query,
    nextToken: offset,
  };
  return addQueryToPath(path, queryParams);
};
